import { motion } from "framer-motion";
import { Link } from "@tanstack/react-router";
import { Award, Clock, ShieldCheck, Users, ArrowRight } from "lucide-react";
import { JSX } from "react/jsx-runtime";

const reasons = [
  {
    icon: Award,
    title: "Certified Excellence",
    description:
      "All our consultants are ICPAK-certified with advanced tax qualifications and continuous professional development.",
  },
  {
    icon: ShieldCheck,
    title: "Proactive Compliance",
    description:
      "We don't wait for issues to arise. Our team monitors regulatory changes and proactively updates your compliance posture.",
  },
  {
    icon: Clock,
    title: "Always On Time",
    description:
      "We have a 99.8% on-time filing record. Missed deadlines cost money — ours never do.",
  },
  {
    icon: Users,
    title: "Dedicated Relationship",
    description:
      "You get a named consultant who knows your business — not a helpdesk ticket.",
  },
];

export function WhyUsSection(): JSX.Element {
  return (
    <section className="py-24 bg-[#FAF7F2] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-[#1A1A1A] leading-tight mb-6">
              Tax Expertise That
              <br />
              <span className=" text-[#6B1E2A]">Works for You</span>
            </h2>
            <p className="text-[#6B6B6B] leading-relaxed mb-8">
              Since our founding, Rosewood Tax Consultancy has been committed to
              one principle: delivering tax services that genuinely make a
              difference. We combine deep technical expertise with practical,
              business-minded advice.
            </p>
            <p className="text-[#6B6B6B] leading-relaxed mb-10">
              Our team of over 20 qualified professionals handles everything
              from basic KRA compliance to complex international tax structures,
              always with absolute professionalism and confidentiality.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#6B1E2A] text-white font-semibold rounded-xl hover:bg-[#4A1219] transition-colors"
              >
                Meet Our Team
                <ArrowRight size={15} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 border border-[#6B1E2A]/20 text-[#6B1E2A] font-semibold rounded-xl hover:border-[#6B1E2A] transition-colors"
              >
                Get in Touch
              </Link>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {reasons.map((reason, i) => {
              const Icon = reason.icon;
              return (
                <motion.div
                  key={reason.title}
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="bg-white rounded-xl p-6 border-[#E8E0D0] hover:border-[#6B1E2A]/20 transition-all"
                >
                  <div className="w-11 h-11 bg-[#6B1E2A]/8 rounded-xl flex items-center justify-center mb-4">
                    <Icon size={20} className="text-[#6B1E2A]" />
                  </div>
                  <h3 className="font-semibold text-[#1A1A1A] mb-2">
                    {reason.title}
                  </h3>
                  <p className="text-sm text-[#6B6B6B] leading-relaxed">
                    {reason.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
